import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {
  EventLink,
  EventLocationType,
  EventStateEnum,
  EventStatusType,
  LinkType,
  Manager,
  Period,
  SelfServiceEvent,
  TicketTypeGet,
  TranslationValue,
  VenueGet,
  VenueType
} from '../../../generated/sales-terminal-client';
import moment from 'moment/moment';
import {cloneDeep} from 'lodash-es';
import {LanguageService} from '../../language/language.service';
import {AuthService} from '../../auth/auth.service';
import {STATUS_CANCELLED, STATUS_DRAFT, STATUS_PAST, STATUS_PRESALE, STATUS_REFUND, STATUS_SUSPENDED} from '../../../modules/sales-terminal/event-search/event-search.constants';
import {Moment} from 'moment';
import {ValidationService} from '../../validation/validation.service';
import {TranslatedCategories} from '../../../modules/back-office/events/bo-event/bo-event.component';
import {DirtyStateTrackerService} from '../../dirty-state-tracker/dirty-state-tracker.service';
import {VENUE_NAME_MAX_LENGTH} from "../../../modules/back-office/venues/view/venues-view.component";

export interface ChangeTracker {
  timeChanged: boolean;
  descriptionsChanged: boolean;
  ticketInfoChanged: boolean;
  ownerChanged: boolean;
  otherChanged: boolean;
}

export interface EventDescriptions {
  title?: TranslationValue[];
  description?: TranslationValue[];
  purchaseDescription?: TranslationValue[];
  ticketDescription?: TranslationValue[];
  saleStoppedDescription?: TranslationValue[];
  eventCancelDescription?: TranslationValue[];
}
export interface EventCategories {
  mainCategoryId: number;
  subCategoryIds: number[];
  lastSavedMainCategoryId: number;
  lastSavedSubCategoryIds: number[];
}
export interface EventDates {
  eventPeriod?: Period;
  salePeriod?: Period;
  publicAvailability?: string;
  doorOpeningTime?: string;
  refundablePeriod: Period;
}

export interface TimeValidations {
  isStartTimeValid?: boolean;
  isEndTimeValid?: boolean;
}
interface DescriptionValidations extends TimeValidations {
  isDescriptionValid?: boolean;
}

interface GeneralValidations extends DescriptionValidations {
  isTitleValid?: boolean;
  isDoorOpeningTimeValid?: boolean;
  isCategoriesValid?: boolean;
  isCoverImageValid?: boolean;
}
interface LocationValidations {
  isLocationLinkValid?: boolean;
  isVenueValid?: boolean;
}
interface TicketsValidations extends DescriptionValidations {
  isTotalTicketsValid?: boolean;
  isMaxTicketsValid?: boolean;
  isVatValid?: boolean;
  isInternetSaleEndValid?: boolean;
  isTicketDescription?: boolean;
  hasTicketTypes?: boolean;
  isRefundPeriodValid?: boolean;
  isReminderDaysBeforeValid?: boolean;
}
interface ExtraValidations {
  areLinksValid?: boolean;
}
interface PublishValidations {
  isPublishDateValid?: boolean;
}

export interface Validations {
  general?: GeneralValidations;
  location?: LocationValidations;
  tickets?: TicketsValidations;
  extra?: ExtraValidations;
  publish?: PublishValidations;
}

export const MAX_SUBCATEGORIES: number = 3;
export const EVENT_DESCRIPTION_MAX_LENGTH: number = 10000;
export const EVENT_TITLE_MAX_LENGTH: number = 72;
export const EVENT_ONLINE_LINK_MAX_LENGTH: number = 42;
export const EVENT_TICKET_TYPE_MAX_LENGTH: number = 46;
export const EVENT_TICKET_TYPE_DESCRIPTION_MAX_LENGTH: number = 60;
export const EVENT_TICKET_DESCRIPTION_MAX_LENGTH: number = 138;
export const MAX_YEARS_IN_THE_FUTURE: number = 5;

@Injectable()
export abstract class SelfServiceEventBaseService extends DirtyStateTrackerService {
  public changeTracker: ChangeTracker;
  public eventChangedWatcher: Subject<any> = new Subject<any>();
  public stepWatcher: Subject<any> = new Subject<any>();
  public warningWatcher: Subject<any> = new Subject<any>();
  public isSandbox: boolean;
  public isRefundable: boolean;
  public hasReminder: boolean;
  public eventOnGoing: boolean;

  public activeEvent: SelfServiceEvent;
  public activeEventChanged: Subject<SelfServiceEvent> = new Subject<SelfServiceEvent>();
  public internalEvent: SelfServiceEvent;
  public isUsingDefaultSalePeriod: boolean;
  public activeEventOriginalValues: {ticketInfo: string, descriptions: string, dates: string};
  public eventCategories: EventCategories;
  public eventVenue: VenueGet;
  public internalVenue: VenueGet;
  public eventTicketTypeList: TicketTypeGet[];
  public ticketInfoInputValues: Record<string, string> = {};
  public activeWarnings: string[];
  public selectedLanguages: string[];
  public eventManagers: Manager[] = [];
  public updatedManagers: string[] = [];
  public updatedOwnerCentreId: number;
  public eventPublishNow: boolean;
  public freshlyCreated: boolean;
  public saveInProgress: boolean;
  public categories: TranslatedCategories = {
    mainCategories: [],
    subCategories: [],
    selectedCategory: null
  };

  public validationsForPublish: Validations;
  public validationsForDraft: Validations;
  public isTicketTypeRangeValid: boolean; // Non-Mandatory

  constructor(protected authService: AuthService,
              protected languageService: LanguageService) {
    super();
  }

  public abstract updateTicketTypeList(event?: SelfServiceEvent, action?: Function): void;
  public abstract checkForWarnings(): void;

  public get eventChanged(): boolean {
    return Object.values(this.changeTracker).some(value => !!value);
  }
  public resetChangeTracker(): void {
    Object.keys(this.changeTracker).forEach(key => {
      (this.changeTracker as any)[key] = false;
    });
  }

  public setActiveEvent(event: SelfServiceEvent): void {
    this.activeEvent = event;
    this.activeEventChanged.next(this.activeEvent);
    this.isUsingDefaultSalePeriod = !this.activeEvent.salePeriod?.start;
    this.eventPublishNow = !event.publicAvailability;
    if (!this.isSandbox || !this.categories?.mainCategories?.length) {
      this.eventCategories = { mainCategoryId: null, subCategoryIds: [], lastSavedMainCategoryId: null, lastSavedSubCategoryIds: [] };
      this.categories = { mainCategories: [], subCategories: [], selectedCategory: null};
    }
    this.eventVenue = {};
    this.eventTicketTypeList = [];
    this.isRefundable = false;
    this.activeWarnings = [];
    this.selectedLanguages = [];
    this.ticketInfoInputValues = {};
    this.dirtyChanged = null;
    this.ticketInfoInputValues['totalTickets'] = this.activeEvent.ticketInfo?.totalTickets?.toString();
    this.ticketInfoInputValues['maxTicketsInWebCart'] = this.activeEvent.ticketInfo?.maxTicketsInWebCart?.toString();
    this.ticketInfoInputValues['vatPercent'] = this.activeEvent.ticketInfo?.vatPercent?.toString();
    this.hasReminder = !!this.activeEvent.reminderDaysBefore;
    this.eventOnGoing = this.isOnSalePassed;
    this.initValidations();
    this.updateTicketTypeList();
  }
  private initValidations(): void {
    this.validationsForDraft = {
      general: { isDoorOpeningTimeValid: true },
      location: { isLocationLinkValid: true },
      tickets: {},
      extra: {},
      publish: {}
    };
    this.validationsForPublish = {
      general: {},
      location: {},
      tickets: {},
      extra: {},
      publish: {}
    };
    this.isTicketTypeRangeValid = true;
    this.changeTracker = {
      timeChanged: false,
      descriptionsChanged: false,
      ticketInfoChanged: false,
      ownerChanged: false,
      otherChanged: false
    };
    this.updateActiveDescriptions();
  }

  public updateActiveDescriptions(): void {
    this.activeEventOriginalValues = {
      ticketInfo: JSON.stringify(this.activeEvent.ticketInfo),
      descriptions: JSON.stringify(this.getDescriptionObjectFromEvent(this.activeEvent)),
      dates: JSON.stringify(this.getEventDatesObjectFromEvent(this.activeEvent))
    };
  }

  private getDescriptionObjectFromEvent(event: SelfServiceEvent): EventDescriptions {
    return {
      title: this.prepTranslation(event.title),
      description: this.prepTranslation(event.description),
      purchaseDescription: this.prepTranslation(event.purchaseDescription),
      ticketDescription: this.prepTranslation(event.ticketDescription),
      saleStoppedDescription: this.prepTranslation(event.saleStoppedDescription),
      eventCancelDescription: this.prepTranslation(event.eventCancelDescription)
    }
  }
  public prepTranslation(translations: TranslationValue[]): TranslationValue[] {
    const filteredTranslations: TranslationValue[] = translations.filter((translation: TranslationValue) => !!translation.value?.length);
    return this.languageService.sortWebShopLanguagesForTranslations(filteredTranslations, this.authService.salesPointLanguage);
  }

  private getEventDatesObjectFromEvent(event: SelfServiceEvent): EventDates {
    return {
      eventPeriod: {
        start: this.prepDate(event.eventPeriod.start),
        end: this.prepDate(event.eventPeriod.end)
      },
      salePeriod: {
        start: this.prepDate(event.salePeriod.start),
        end: this.prepDate(event.salePeriod.end)
      },
      doorOpeningTime: this.prepDate(event.doorOpeningTime),
      publicAvailability: this.prepDate(event.publicAvailability),
      refundablePeriod: {
        start: this.prepDate(event.refundablePeriod.start),
        end: this.prepDate(event.refundablePeriod.end)
      }
    }
  }
  public prepDate(date: string): string {
    return moment(cloneDeep(date || null)).format();
  }

  public isGeneralValid(forPublish?: boolean): boolean {
    if (forPublish) {
      return Object.values(this.validationsForPublish.general).every(v => !!v);
    }
    return Object.values(this.validationsForDraft.general).every(v => !!v);
  }
  public isLocationValid(forPublish?: boolean): boolean {
    if (forPublish) {
      return Object.values(this.validationsForPublish.location).every(v => !!v);
    }
    return Object.values(this.validationsForDraft.location).every(v => !!v);
  }
  public isTicketsValid(forPublish?: boolean): boolean {
    if (forPublish) {
      return Object.values(this.validationsForPublish.tickets).every(v => !!v);
    }
    return Object.values(this.validationsForDraft.tickets).every(v => !!v);
  }
  public isExtraValid(forPublish?: boolean): boolean {
    if (forPublish) {
      return Object.values(this.validationsForPublish.extra).every(v => !!v);
    }
    return Object.values(this.validationsForDraft.extra).every(v => !!v);
  }
  public isPublishValid(): boolean {
    return Object.values(this.validationsForPublish.publish).every(v => !!v);
  }

  public get defaultValidations(): Validations {
    if (this.isEventPublished) {
      return this.validationsForPublish;
    }
    return this.validationsForDraft;
  }

  public get isEventPublished(): boolean {
    return (this.activeEvent?.state || []).includes(EventStateEnum.Pending) || this.activeEvent?.status?.id !== STATUS_DRAFT;
  }

  public get isValidForSave(): boolean {
    if (this.isEventPublished) {
      return this.isValidForPublish;
    }
    return this.isValidForDraft;
  }
  public get isValidForDraft(): boolean {
    return this.isGeneralValid() && this.isLocationValid() && this.isTicketsValid() && this.isExtraValid();
  }
  public get isValidForPublish(): boolean {
    return !!(!this.authService.user.isPrivateAccount && this.activeEvent?.locationType && this.activeEvent?.timezone && this.isGeneralValid(true) &&
        this.isLocationValid(true) && this.isTicketsValid(true) && this.isExtraValid(true) && this.isPublishValid() && !this.changeTracker.ownerChanged) &&
      !this.authService.isBankAccountUnverified;
  }

  public validateEvent(): void {
    this.validateTime();
    this.validateDescriptions();
    this.validateLocationLink();
    this.validateCategories();
    this.validateLinks();
    this.validateVenue();
    this.validateTicketInfo();
    this.validateImages();
  }

  public validateTime(): void {
    this.validationsForDraft.general.isStartTimeValid = this.validateStart();
    this.validationsForDraft.general.isEndTimeValid = this.validateEnd();
    this.validationsForDraft.general.isDoorOpeningTimeValid = this.validateDoorOpening();
    this.validationsForDraft.tickets.isStartTimeValid = this.validateSaleStart();
    this.validationsForDraft.tickets.isEndTimeValid = this.validateSaleEnd();
    this.validationsForDraft.tickets.isInternetSaleEndValid = this.validateInternetSaleEnd();
    this.validationsForDraft.publish.isPublishDateValid = this.validatePublishDate();

    this.validationsForPublish.general.isStartTimeValid = this.validateStart(true);
    this.validationsForPublish.general.isEndTimeValid = this.validateEnd(true);
    this.validationsForPublish.general.isDoorOpeningTimeValid = this.validationsForDraft.general.isDoorOpeningTimeValid;
    this.validationsForPublish.tickets.isStartTimeValid = this.validateSaleStart(true);
    this.validationsForPublish.tickets.isEndTimeValid = this.validateSaleEnd(true);
    this.validationsForPublish.tickets.isInternetSaleEndValid = this.validationsForDraft.tickets.isInternetSaleEndValid;
    this.validationsForPublish.publish.isPublishDateValid = this.validationsForDraft.publish.isPublishDateValid;
    this.validationsForPublish.tickets.isRefundPeriodValid = this.validateRefundPeriod();
    this.changeTracker.timeChanged = JSON.stringify(this.getEventDatesObjectFromEvent(this.activeEvent)) !== this.activeEventOriginalValues.dates;
  }

  public validateNotTooFarInFuture(date: Moment): boolean {
    const future: string = moment.utc().add(MAX_YEARS_IN_THE_FUTURE, 'years').toISOString();
    return date.isBefore(moment.utc(future));
  }

  private validateStart(forPublish?: boolean): boolean {
    if (!this.activeEvent.eventPeriod?.start) {
      return !forPublish;
    }
    const start: moment.Moment = moment(this.activeEvent.eventPeriod?.start || null);
    const end: moment.Moment = moment(this.activeEvent.eventPeriod?.end || null);
    const now: moment.Moment = moment(new Date());
    if (start?.isValid()) {
      if ((this.activeEvent.status?.id !== STATUS_DRAFT || start.isAfter(now)) && this.validateNotTooFarInFuture(moment.utc(start))) {
        if (end?.isValid()) {
          return start.isBefore(end);
        }
        return true;
      }
    }
    return false;
  }
  private validateEnd(forPublish?: boolean): boolean {
    if (!this.activeEvent.eventPeriod?.end) {
      return !forPublish;
    }
    const end: moment.Moment = moment.utc(this.activeEvent.eventPeriod?.end || null);
    return end?.isValid() && end.isAfter(moment.utc(this.activeEvent.eventPeriod?.start)) && this.validateNotTooFarInFuture(moment.utc(end));
  }
  private validateDoorOpening(): boolean {
    const doorOpen: moment.Moment = moment.utc(this.activeEvent.doorOpeningTime || null);
    const start: moment.Moment = moment.utc(this.activeEvent.eventPeriod?.start || null);
    return !this.activeEvent.doorOpeningTime || (doorOpen?.isValid() && doorOpen.isBefore(start));
  }

  private validateSaleStart(forPublish?: boolean): boolean {
    if (!this.activeEvent.salePeriod?.start) {
      return !forPublish;
    }
    const start: moment.Moment = moment(this.activeEvent.eventPeriod?.start || null);
    const saleStart: moment.Moment = moment(this.activeEvent.salePeriod?.start || null);
    const saleEnd: moment.Moment = moment(this.activeEvent.salePeriod?.end || null);
    const now: moment.Moment = moment(new Date());
    if (saleStart?.isValid()) {
      if (this.activeEvent.status?.id !== STATUS_DRAFT || saleStart.isAfter(now)) {
        if (start?.isValid() && saleStart.isSameOrBefore(start)) {
          if (saleEnd?.isValid()) {
            return saleStart.isBefore(saleEnd);
          }
          return true;
        }
      }
    }
    return false;
  }
  private validateSaleEnd(forPublish?: boolean): boolean {
    if (!this.activeEvent.salePeriod?.end) {
      return !forPublish;
    }
    const end: moment.Moment = moment(this.activeEvent.eventPeriod?.end);
    const saleStart: moment.Moment = moment(this.activeEvent.salePeriod?.start || null);
    const saleEnd: moment.Moment = moment(this.activeEvent.salePeriod?.end || null);
    return saleEnd?.isValid() && saleStart.isValid() && saleEnd.isAfter(saleStart) && end?.isValid() && saleEnd.isSameOrBefore(end);
  }
  private validateInternetSaleEnd(): boolean {
    const internetSaleEnd = moment.utc(this.activeEvent.internetSaleEnd || null);
    return !this.activeEvent.internetSaleEnd || (
      internetSaleEnd?.isValid() &&
      internetSaleEnd.isSameOrBefore(moment.utc(this.activeEvent.salePeriod?.end)) &&
      internetSaleEnd.isAfter(moment.utc(this.activeEvent.salePeriod?.start))
    );
  }

  private validateRefundPeriod(): boolean {
    if (!this.isRefundable) {
      return true;
    }
    const refundStartDate = moment.utc(this.activeEvent.refundablePeriod?.start);
    const refundEndDate = moment.utc(this.activeEvent.refundablePeriod?.end);

    return !!this.activeEvent?.refundablePeriod.start && !!this.activeEvent.refundablePeriod?.end &&
      refundEndDate.isSameOrAfter(refundStartDate);
  }

  public validatePublishDate(): boolean {
    if (!this.activeEvent.id) return;
    const publish: moment.Moment = moment.utc(this.activeEvent.publicAvailability || null);
    const now: moment.Moment = moment.utc(new Date());
    if (![STATUS_DRAFT, STATUS_SUSPENDED].includes(this.activeEvent?.status?.id) && publish.isBefore(now)) {
      return true;
    }
    const saleEnd: moment.Moment = moment.utc(this.activeEvent.salePeriod?.end || null);

    if (!this.activeEvent.publicAvailability) return true;
    if (publish?.isValid()) {
      return publish.isSameOrBefore(saleEnd);
    }
    return false;
  }

  public validateDescriptions(): void {
    this.validationsForDraft.general.isTitleValid = this.areTranslationsValid(this.activeEvent.title, EVENT_TITLE_MAX_LENGTH, false, true);
    this.validationsForDraft.general.isDescriptionValid = this.areTranslationsValid(this.activeEvent.description, EVENT_DESCRIPTION_MAX_LENGTH, true);
    this.validationsForDraft.tickets.isDescriptionValid = this.areTranslationsValid(this.activeEvent.purchaseDescription, EVENT_DESCRIPTION_MAX_LENGTH, true);
    this.validationsForDraft.tickets.isTicketDescription = this.areTranslationsValid(this.activeEvent.ticketDescription, EVENT_TICKET_DESCRIPTION_MAX_LENGTH, true);
    this.validationsForPublish.general.isDescriptionValid = this.areTranslationsValid(this.activeEvent.description, EVENT_DESCRIPTION_MAX_LENGTH, true, true);
    this.validationsForPublish.general.isTitleValid = this.validationsForDraft.general.isTitleValid;
    if (this.activeEvent?.status?.id === STATUS_SUSPENDED && this.activeEvent.saleStoppedDescription?.length) {
      this.validationsForPublish.general.isDescriptionValid = this.validationsForPublish.general.isDescriptionValid &&
        this.areTranslationsValid(this.activeEvent.saleStoppedDescription, EVENT_DESCRIPTION_MAX_LENGTH, true);
    }
    if (this.activeEvent?.status?.id === STATUS_CANCELLED) {
      this.validationsForPublish.general.isDescriptionValid = this.validationsForPublish.general.isDescriptionValid &&
        this.areTranslationsValid(this.activeEvent.eventCancelDescription, EVENT_DESCRIPTION_MAX_LENGTH, true, true);
    }
    this.changeTracker.descriptionsChanged = JSON.stringify(this.getDescriptionObjectFromEvent(this.activeEvent)) !== this.activeEventOriginalValues.descriptions;
  }
  public areTranslationsValid(translations: TranslationValue[], maxLength: number = EVENT_DESCRIPTION_MAX_LENGTH, useModelAsText?: boolean, required?: boolean): boolean {
    if (required && !translations.length) {
      return false;
    }
    let valid: boolean = true;
    this.selectedLanguages.forEach((language: string) => {
      if (valid) {
        const translation: TranslationValue = translations.find((translation: TranslationValue) => translation.language === language);
        if (required && language === this.authService.salesPointLanguage) {
          valid = !!(translation?.value && this.isTextValid(translations, language, maxLength, useModelAsText));
        } else {
          valid = this.isTextValid(translations, language, maxLength, useModelAsText);
        }
      }
    });
    return valid;
  }
  private isTextValid(translations: TranslationValue[], language: string, maxLength: number = EVENT_DESCRIPTION_MAX_LENGTH, useModelAsText?: boolean): boolean {
    let text: string = translations?.find((translation: TranslationValue) => translation.language === language)?.value;
    if (useModelAsText) {
      text = cloneDeep(ValidationService.getStringModelAsText(text));
    }
    return !text?.length || (ValidationService.validateStringIsNotEmpty(text) && ValidationService.validateString(text, 0, maxLength));
  }

  public validateLocationLink(event = this.activeEvent): void {
    if (this.eventChanged) {
      if (this.internalEvent) {
        event = this.internalEvent;
      }
    }
    this.validationsForDraft.location.isLocationLinkValid = true;
    if (event.locationType !== EventLocationType.Venue) {
      this.validationsForPublish.location.isLocationLinkValid = false;
      event.links.forEach((link: EventLink) => {
        if (link.type === LinkType.OnlineLocation) {
          this.validationsForDraft.location.isLocationLinkValid = ValidationService.validateUrl(link.value) && link.value?.length <= EVENT_ONLINE_LINK_MAX_LENGTH;
          this.validationsForPublish.location.isLocationLinkValid = link.value?.length && this.validationsForDraft.location.isLocationLinkValid;
        }
      });
    } else {
      this.validationsForPublish.location.isLocationLinkValid = true;
    }
  }

  public validateCategories(): void {
    const subCategoriesValid: boolean = !this.eventCategories.subCategoryIds?.length || this.eventCategories.subCategoryIds.length <= MAX_SUBCATEGORIES;
    this.validationsForDraft.general.isCategoriesValid = !this.eventCategories.mainCategoryId || subCategoriesValid;
    this.validationsForPublish.general.isCategoriesValid = !!this.eventCategories.mainCategoryId && subCategoriesValid;
  }

  public validateLinks(): void {
    this.validationsForDraft.extra.areLinksValid = true;
    this.activeEvent.links.forEach((link: EventLink) => {
      if (this.validationsForDraft.extra.areLinksValid && link.type !== LinkType.OnlineLocation) {
        this.validationsForDraft.extra.areLinksValid = ValidationService.validateUrl(link.value);
      }
    });
    this.validationsForPublish.extra.areLinksValid = this.validationsForDraft.extra.areLinksValid;
  }

  public validateVenue(venue = this.eventVenue, event = this.activeEvent): void {
    if (this.eventChanged) {
      if (this.internalEvent) {
        event = this.internalEvent;
      }
      if (this.internalVenue) {
        venue = this.internalVenue;
      }
    }
    const venueFields: string[] = [venue.name, venue.city, venue.countryCode];
    const venueNameValid: boolean = venue.name?.length <= VENUE_NAME_MAX_LENGTH;
    const venueFieldsAllFilled: boolean = venueFields.every(v => !!v?.length) && venueNameValid;
    const venueFieldsAllEmpty: boolean = venueFields.every(v => !v?.length);
    this.validationsForDraft.location.isVenueValid =
      (event.locationType === EventLocationType.Venue || this.validationsForDraft.location.isLocationLinkValid) &&
      (event.locationType === EventLocationType.Online || venueFieldsAllEmpty || venueFieldsAllFilled);
    this.validationsForPublish.location.isVenueValid =
      (event.locationType === EventLocationType.Venue || this.validationsForPublish.location.isLocationLinkValid) &&
      (event.locationType === EventLocationType.Online || venueFieldsAllFilled);
  }

  private validateTicketInfo(): void {
    this.validationsForDraft.tickets.isTotalTicketsValid = this.validateAndAssignTicketInfoValue(this.ticketInfoInputValues['totalTickets'], 'totalTickets', 10000);
    this.validationsForPublish.tickets.isTotalTicketsValid = this.activeEvent.ticketInfo?.totalTickets > 0;
    this.validationsForDraft.tickets.isMaxTicketsValid = this.validateAndAssignTicketInfoValue(this.ticketInfoInputValues['maxTicketsInWebCart'], 'maxTicketsInWebCart', 100);
    this.validationsForPublish.tickets.isMaxTicketsValid = this.activeEvent.ticketInfo.maxTicketsInWebCart > 0 && this.activeEvent.ticketInfo.maxTicketsInWebCart <= 100;
    this.validationsForDraft.tickets.isVatValid = this.validateAndAssignTicketInfoValue(this.ticketInfoInputValues['vatPercent'], 'vatPercent', 100);
    this.validationsForPublish.tickets.isVatValid = this.validationsForDraft.tickets.isVatValid;
    this.validationsForDraft.tickets.isReminderDaysBeforeValid = this.activeEvent.reminderDaysBefore === null || this.activeEvent.reminderDaysBefore >= 1 && this.activeEvent.reminderDaysBefore <= 7;
    this.validationsForPublish.tickets.isReminderDaysBeforeValid = this.validationsForDraft.tickets.isReminderDaysBeforeValid;
  }
  public validateAndAssignTicketInfoValue(inputValue: string, eventField: string, maxLimit: number): boolean {
    if (this.activeEvent.ticketInfo) {
      const isInputValid: boolean = eventField == 'vatPercent' ? ValidationService.validatePercentage(inputValue, true) : ValidationService.validateNumberFromString(inputValue);
      if (isInputValid) {
        const numValue: number = Number(inputValue);
        if (!numValue || (numValue > 0 && numValue <= maxLimit)) {
          (this.activeEvent.ticketInfo as any)[eventField] = inputValue?.length ? numValue : null;
          this.changeTracker.ticketInfoChanged = JSON.stringify(this.activeEvent.ticketInfo) !== this.activeEventOriginalValues.ticketInfo;
          return true;
        }
      }
    }
    if ((this.activeEvent.ticketInfo as any)[eventField] != null) {
      (this.activeEvent.ticketInfo as any)[eventField] = null;
      this.changeTracker.ticketInfoChanged = JSON.stringify(this.activeEvent.ticketInfo) !== this.activeEventOriginalValues.ticketInfo;
    }
    return false;
  }

  private validateImages(): void {
    this.validationsForPublish.general.isCoverImageValid = this.isSandbox || !!this.activeEvent.imageId;
  }

  protected sortTicketTypes(ticketTypesList: TicketTypeGet[]): TicketTypeGet[] {
    return ticketTypesList.sort((a: TicketTypeGet, b: TicketTypeGet) => a.price - b.price);
  }
  protected validateTicketTypeRange(ticketTypeList: TicketTypeGet[] = this.eventTicketTypeList): void {
    if (ticketTypeList?.length) {
      this.validationsForPublish.tickets.hasTicketTypes = true;
      this.isTicketTypeRangeValid = true;
      if (!ticketTypeList.some((ticketType: TicketTypeGet) => ticketType.usesEventSalesPeriod)) {
        this.isTicketTypeRangeValid = true;
        const typesWithCustomPeriod: TicketTypeGet[] = ticketTypeList.filter((ticketType: TicketTypeGet) => !ticketType.usesEventSalesPeriod);
        if (typesWithCustomPeriod.length) {
          const availabilityPeriod: Period = cloneDeep(typesWithCustomPeriod[0].availabilityPeriod);
          if (typesWithCustomPeriod.length > 1) {
            typesWithCustomPeriod.forEach((ticketType: TicketTypeGet) => {
              if (this.isTicketTypeRangeValid) {
                const periodStart: moment.Moment = moment(availabilityPeriod?.start || null);
                const periodEnd: moment.Moment = moment(availabilityPeriod?.end || null);
                const typeStart: moment.Moment = moment(ticketType.availabilityPeriod?.start || null);
                const typeEnd: moment.Moment = moment(ticketType.availabilityPeriod?.end || null);
                if (periodStart.isValid() && periodEnd.isValid() && typeStart.isValid() && typeEnd.isValid()) {
                  if (typeStart.isBefore(periodStart)) {
                    availabilityPeriod.start = ticketType.availabilityPeriod.start;
                  }
                  if (typeEnd.isAfter(periodEnd)) {
                    availabilityPeriod.end = ticketType.availabilityPeriod.end;
                  }
                } else {
                  this.isTicketTypeRangeValid = false;
                }
              }
            });
          }
          if (this.isTicketTypeRangeValid) {
            const saleStart: moment.Moment = moment(this.activeEvent.salePeriod?.start || null);
            const saleEnd: moment.Moment = moment(this.activeEvent.salePeriod?.end || null);
            const typeStart: moment.Moment = moment(availabilityPeriod?.start || null);
            const typeEnd: moment.Moment = moment(availabilityPeriod?.end || null);
            if (saleStart.isValid() && saleEnd.isValid() && typeStart.isValid() && typeEnd.isValid()) {
              this.isTicketTypeRangeValid = typeStart.isSameOrAfter(saleStart) && typeEnd.isSameOrBefore(saleEnd)
            } else {
              this.isTicketTypeRangeValid = false;
            }
          }
        }
      }
    } else {
      this.validationsForPublish.tickets.hasTicketTypes = false;
      this.isTicketTypeRangeValid = false;
    }
  }

  public getEventStatusTypeFromId(statusId: number): EventStatusType {
    switch (statusId) {
      case STATUS_DRAFT:
        return EventStatusType.Draft;
      case STATUS_CANCELLED:
      case STATUS_SUSPENDED:
        return EventStatusType.SaleStopped;
      case STATUS_PAST:
        return EventStatusType.Past;
      default:
        return EventStatusType.OnSale;
    }
  }

  public get isOnSalePassed(): boolean {
    const start: moment.Moment = moment.utc(this.activeEvent?.eventPeriod?.start || null);
    return !this.isDraft && !this.isSuspended && start.isValid() && start.isSameOrBefore(moment.utc(), 'minute');
  }

  public get isInNonEditableState(): boolean {
    return this.isCancelled || this.isPastEvent;
  }

  public get isCancelled(): boolean {
    return this.activeEvent?.status?.id === STATUS_CANCELLED;
  }

  public get isPastEvent(): boolean {
    return this.activeEvent?.status?.id === STATUS_PAST;
  }

  public get isSuspended(): boolean {
    return this.activeEvent?.status?.id === STATUS_SUSPENDED;
  }

  public get isDraft(): boolean {
    return this.activeEvent?.status?.id === STATUS_DRAFT;
  }

  public get isRefund(): boolean {
    return this.activeEvent?.status?.id === STATUS_REFUND;
  }

  public get isPresale(): boolean {
    return this.activeEvent?.status?.id === STATUS_PRESALE;
  }

  protected checkIfPrivatePerson(): void {
    if (this.authService.user?.isPrivateAccount) {
      this.activeWarnings.push('general.api_events_private_account_warning');
    }
  }
  public checkConsentWarning(): boolean {
    const isNotOnline = this.internalEvent?.locationType !== EventLocationType.Online;
    const isNotCancelledOrPast = this.activeEvent.status?.id !== STATUS_CANCELLED && this.activeEvent.status?.id !== STATUS_PAST;
    const isVenuePublicWithConsent = this.internalVenue?.type === VenueType.Public && this.internalVenue?.ownerConsentRequired;
    const isPendingWithConsent = this.internalVenue?.ownerConsentRequired && this.activeEvent.state?.includes(EventStateEnum.Pending);

    if (isNotOnline && ((isVenuePublicWithConsent && isNotCancelledOrPast) || isPendingWithConsent)) {
      if (!this.activeWarnings.includes('general.api_events_venue_consent_required_info')) {
        this.activeWarnings.push('general.api_events_venue_consent_required_info');
      }
      return true;
    }
    return false;
  }
}
